/**
 * CompanyAuditPeriodPanel — Phase 2 / Phase 5 surface
 *
 * Shows the company admin:
 *   - Any open audit period (with cancel option, only if no certificate yet)
 *   - A "Request External Audit" button per active framework assignment
 *   - On click → opens audit period → all in-scope pages get locked
 *
 * See docs/COMPLIANCE_LIFECYCLE_PLAN.md §3 Phase E and §6 Stage 5.
 */

import React, { useEffect, useState } from 'react';
import { ShieldCheck, Lock, X } from 'lucide-react';
import { auditPeriodApi } from './VerificationPanel';
import { notify } from '@/core/notifications/notify';

interface FrameworkAssignmentLite {
  id: number;
  framework_id: number;
  framework_name: string;
  framework_version: string;
}

interface Props {
  /** Active framework assignments for the current company */
  assignments?: FrameworkAssignmentLite[];
}

export const CompanyAuditPeriodPanel: React.FC<Props> = ({ assignments = [] }) => {
  const [periods, setPeriods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const list = await auditPeriodApi.listForCompany();
      setPeriods(list);
    } catch {
      // non-blocking
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const openPeriods = periods.filter((p) => p.status === 'open' || p.status === 'locked');
  const assignmentsWithoutOpenPeriod = assignments.filter(
    (a) => !openPeriods.some((p) => p.framework_assignment_id === a.id),
  );

  const handleOpen = async (assignmentId: number, frameworkName: string) => {
    if (
      !window.confirm(
        `Open an external audit period for "${frameworkName}"?\n\n` +
          `This will LOCK all compliance pages for this framework. ` +
          `Compliance users will not be able to edit pages until the audit closes ` +
          `(by certificate issuance) or is cancelled.`,
      )
    )
      return;
    try {
      const result = await auditPeriodApi.open(assignmentId);
      if (!result.success) {
        notify.error(result.message ?? 'Failed to open audit period');
        return;
      }
      notify.success(
        `Audit period opened. ${result.data?.pagesLocked ?? 0} pages locked for audit.`,
      );
      await reload();
    } catch (e: any) {
      notify.error(e?.message ?? 'Failed to open audit period');
    }
  };

  const handleCancel = async (id: number) => {
    const reason = window.prompt('Reason for cancelling this audit period?');
    if (!reason) return;
    try {
      const result = await auditPeriodApi.cancel(id, reason);
      if (!result.success) {
        notify.error(result.message ?? 'Cancel failed');
        return;
      }
      notify.success('Audit period cancelled. Pages unlocked.');
      await reload();
    } catch (e: any) {
      notify.error(e?.message ?? 'Cancel failed');
    }
  };

  if (loading && periods.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={18} className="text-indigo-600" />
        <h3 className="text-sm font-bold text-slate-800">External Audit</h3>
      </div>

      {openPeriods.length > 0 && (
        <div className="space-y-2 mb-4">
          {openPeriods.map((p: any) => (
            <div
              key={p.id}
              className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Lock size={14} className="text-amber-700 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-bold text-amber-900">
                    Audit period #{p.id} — {p.status.toUpperCase()}
                  </p>
                  <p className="text-[11px] text-amber-700">
                    Assignment {p.framework_assignment_id} · opened{' '}
                    {new Date(p.opened_at).toLocaleString()}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleCancel(p.id)}
                className="text-amber-700 hover:text-amber-900 p-1"
                title="Cancel audit period (only allowed before a certificate has been issued)"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {assignmentsWithoutOpenPeriod.length === 0 && openPeriods.length === 0 ? (
        <p className="text-xs text-slate-500">
          No active framework assignments to audit.
        </p>
      ) : (
        <div className="space-y-2">
          {assignmentsWithoutOpenPeriod.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-800 truncate">
                  {a.framework_name}
                </p>
                <p className="text-[11px] text-slate-500">v{a.framework_version}</p>
              </div>
              <button
                onClick={() => handleOpen(a.id, a.framework_name)}
                className="px-3 py-1 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
              >
                Request Audit
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] text-slate-400 mt-3 italic">
        Opening an audit period locks pages from edits until the certificate is issued.
      </p>
    </div>
  );
};
